import type { Application } from '@/domains/application/application.types';
import type { CheckpointParameter } from '@/domains/checkpoint/checkpoint.types';
import { CheckpointParameterType } from '@/domains/checkpoint/checkpoint.types';
import type { EvaluationView } from '@/domains/checkpoint/checkpointViewAccess';
import type {
  Evaluation,
  EvaluationAssessmentDataItem,
  EvaluationCheckpoint,
  EvaluationExceptionDetails,
  EvaluationItem,
  EvaluationStatus,
} from '@/domains/evaluation/evaluation.types';
import { AutomationPlatform } from '@/domains/evaluation/evaluation.types';

import type {
  SiteComplianceCheckpointMaintenanceCriticality,
  SiteComplianceCheckpointOperationalStatus,
  SiteComplianceCheckpointReleaseStatus,
  SiteComplianceCheckpointSettingsRow,
  SiteComplianceCheckpointUnscoredStatusDetails,
} from '../siteComplianceSettings.types';
import type {
  SiteComplianceNetworkTier,
  SiteComplianceSite,
  SiteComplianceSiteType,
} from '../siteCompliance.types';
import type {
  AssessmentDto,
  AssessmentDataDto,
  AssessmentItemDto,
  CheckpointMetadataDto,
  CheckpointSettingDto,
  CheckpointStatusWindowDto,
  EvaluationDetailDto,
  EvaluationSiteSnapshotDto,
  EvaluationSummaryDto,
  EvaluationSummaryPreviousDto,
  JobStatusResponse,
  JobStatusResponseDto,
  SdkCheckpointParameterDto,
  SiteComplianceAssessmentData,
  SiteComplianceJobOutput,
  SiteComplianceJobStatus,
  SeedCheckpointsResponse,
  SeedCheckpointsResponseDto,
  SiteComplianceJson,
  SubmitEvaluationResponse,
  SubmitEvaluationResponseDto,
} from './types';
import type {
  SiteComplianceEvaluation,
  SiteComplianceEvaluationCounts,
  SiteComplianceEvaluationDetails,
  SiteComplianceEvaluationPrevious,
} from '../siteComplianceEvaluation.types';

const DEFAULT_VIEWS: EvaluationView[] = ['default', 'network'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const asString = (value: unknown, fallback = '') => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return `${value}`;
  }

  return fallback;
};

const asNullableString = (value: unknown) => {
  const text = asString(value).trim();
  return text || null;
};

const asNumber = (value: unknown, fallback = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }

  return fallback;
};

const asBoolean = (value: unknown, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }

  return fallback;
};

const toArray = <T>(value: T[] | T | null | undefined): T[] => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined) return [];
  return [value];
};

const normalizeScore = (value: unknown) =>
  Math.round(asNumber(value, 0) * 100) / 100;

const normalizeEvaluationStatus = (status: unknown): EvaluationStatus => {
  const normalized = asString(status, 'NA').trim().toUpperCase();

  if (normalized === 'PASS' || normalized === 'PASSED') return 'PASS';
  if (normalized === 'FAIL' || normalized === 'FAILED') return 'FAIL';
  if (normalized === 'EXCEPTION' || normalized === 'EXEMPTED') {
    return 'EXCEPTION';
  }
  if (normalized === 'ERROR') return 'ERROR';

  return 'NA';
};

const DEFAULT_EVALUATION_COUNTS: SiteComplianceEvaluationCounts = {
  error: 0,
  exceptions: 0,
  failed: 0,
  passed: 0,
  scored: 0,
  skipped: 0,
  total: 0,
  unscored: 0,
};

const firstFiniteCount = (
  record: Record<string, unknown>,
  keys: string[],
): number | null => {
  for (const key of keys) {
    const value = record[key];

    if (value === null || value === undefined) {
      continue;
    }

    const count = asNumber(value, Number.NaN);

    if (Number.isFinite(count)) {
      return count;
    }
  }

  return null;
};

const toEvaluationCountsFromSummary = (
  summaryDto: EvaluationSummaryDto,
): SiteComplianceEvaluationCounts => {
  const record = summaryDto as Record<string, unknown>;
  const scored =
    firstFiniteCount(record, [
      'scored',
      'scoredCheckpoints',
      'totalScoredCheckpoints',
    ]) ?? DEFAULT_EVALUATION_COUNTS.scored;
  const explicitUnscored = firstFiniteCount(record, [
    'unscored',
    'unscoredCheckpoints',
    'preview',
    'previewCheckpoints',
  ]);
  const total =
    firstFiniteCount(record, ['total', 'totalCheckpoints']) ??
    scored + (explicitUnscored ?? 0);
  const unscored = explicitUnscored ?? Math.max(total - scored, 0);

  return {
    error:
      firstFiniteCount(record, ['error', 'errors', 'errorCheckpoints']) ??
      DEFAULT_EVALUATION_COUNTS.error,
    exceptions:
      firstFiniteCount(record, ['exceptions', 'exceptionCount']) ??
      DEFAULT_EVALUATION_COUNTS.exceptions,
    failed:
      firstFiniteCount(record, ['failed', 'failedCheckpoints']) ??
      DEFAULT_EVALUATION_COUNTS.failed,
    passed:
      firstFiniteCount(record, ['passed', 'passedCheckpoints']) ??
      DEFAULT_EVALUATION_COUNTS.passed,
    scored,
    skipped:
      firstFiniteCount(record, ['skipped', 'skippedCheckpoints']) ??
      unscored,
    total,
    unscored,
  };
};

const deriveEvaluationCountsFromCheckpoints = (
  checkpoints: EvaluationCheckpoint[],
): SiteComplianceEvaluationCounts =>
  checkpoints.reduce<SiteComplianceEvaluationCounts>(
    (counts, checkpoint) => {
      const status = normalizeEvaluationStatus(checkpoint.status);
      const hasException =
        Boolean(checkpoint.hasException) || status === 'EXCEPTION';
      const isScored = Boolean(checkpoint.metadata.isScored);

      return {
        error: counts.error + (status === 'ERROR' ? 1 : 0),
        exceptions: counts.exceptions + (hasException ? 1 : 0),
        failed: counts.failed + (status === 'FAIL' ? 1 : 0),
        passed: counts.passed + (status === 'PASS' ? 1 : 0),
        scored: counts.scored + (isScored ? 1 : 0),
        skipped: counts.skipped + (status === 'NA' ? 1 : 0),
        total: counts.total + 1,
        unscored: counts.unscored + (isScored ? 0 : 1),
      };
    },
    { ...DEFAULT_EVALUATION_COUNTS },
  );

const normalizeMaintenanceCriticality = (
  value: unknown,
): SiteComplianceCheckpointMaintenanceCriticality | null => {
  const normalized = asString(value).trim().toUpperCase();

  if (
    normalized === 'CRITICAL' ||
    normalized === 'HIGH' ||
    normalized === 'MEDIUM'
  ) {
    return normalized;
  }

  return null;
};

const getWindowValue = (
  window: CheckpointStatusWindowDto | null | undefined,
  camelKey: keyof CheckpointStatusWindowDto,
  snakeKey: keyof CheckpointStatusWindowDto,
) => window?.[camelKey] ?? window?.[snakeKey] ?? null;

const normalizeUnscoredType = (
  window: CheckpointStatusWindowDto | null | undefined,
) =>
  asString(
    window?.unscoredType ??
      window?.unscored_type ??
      window?.type,
  )
    .trim()
    .toLowerCase();

const getStatusWindow = (
  value: unknown,
): CheckpointStatusWindowDto | null => {
  if (!isRecord(value)) return null;
  return value as CheckpointStatusWindowDto;
};

const isDisabledStatus = (value: unknown) => {
  if (typeof value === 'boolean') return value;
  if (!isRecord(value)) return false;
  return Object.keys(value).length > 0;
};

const getUnscoredStatusWindow = (dto: CheckpointSettingDto) => {
  const status = dto.status;
  const unscoredValue = status?.unScored ?? status?.unscored;
  return getStatusWindow(unscoredValue);
};

const buildSettingsStatusDetails = (
  dto: CheckpointSettingDto,
): SiteComplianceCheckpointUnscoredStatusDetails | null => {
  const status = dto.status;
  const disabled = isDisabledStatus(status?.disabled);

  if (!asBoolean(dto.isEnabled, false) || disabled) {
    const disabledWindow = getStatusWindow(status?.disabled);

    return {
      criticality: null,
      endDate: null,
      message:
        asNullableString(disabledWindow?.message) ??
        'This checkpoint version is disabled and will not be scored.',
      mode: 'DISABLED',
      reason:
        asString(disabledWindow?.reason).trim() ||
        'Checkpoint is disabled in Site Compliance.',
      startDate: asNullableString(
        getWindowValue(disabledWindow, 'startDate', 'start_date'),
      ),
    };
  }

  if (asBoolean(dto.isScored, false)) {
    return null;
  }

  const unscoredWindow = getUnscoredStatusWindow(dto);
  const unscoredType = normalizeUnscoredType(unscoredWindow);
  const mode: Exclude<SiteComplianceCheckpointOperationalStatus, 'ACTIVE'> =
    unscoredType === 'maintenance' ? 'MAINTENANCE' : 'PREVIEW';
  const endDate = asNullableString(
    getWindowValue(unscoredWindow, 'endDate', 'end_date'),
  );
  const reason =
    asString(unscoredWindow?.reason).trim() ||
    'Checkpoint is currently not scored.';

  return {
    criticality: normalizeMaintenanceCriticality(unscoredWindow?.criticality),
    endDate,
    message:
      asNullableString(unscoredWindow?.message) ??
      (mode === 'MAINTENANCE'
        ? 'This checkpoint is in maintenance and is not currently scored.'
        : 'This checkpoint is in preview and is not currently scored.'),
    mode,
    reason,
    startDate: asNullableString(
      getWindowValue(unscoredWindow, 'startDate', 'start_date'),
    ),
  };
};

const normalizeViews = (views: unknown): EvaluationView[] => {
  if (!Array.isArray(views)) return DEFAULT_VIEWS;

  const normalizedViews = views.reduce<EvaluationView[]>((acc, view) => {
    const normalized = asString(view).trim().toLowerCase();

    if (
      (normalized === 'default' ||
        normalized === 'dr' ||
        normalized === 'network' ||
        normalized === 'nova') &&
      !acc.includes(normalized)
    ) {
      acc.push(normalized);
    }

    return acc;
  }, []);

  return normalizedViews.length ? normalizedViews : DEFAULT_VIEWS;
};

const normalizeReleaseStatus = (
  value: unknown,
): SiteComplianceCheckpointReleaseStatus => {
  const normalized = asString(value).trim().toLowerCase();

  if (
    normalized === 'merged_to_dev' ||
    normalized === 'dev_testing' ||
    normalized === 'ready_for_stage' ||
    normalized === 'local_testing' ||
    normalized === 'pr_review'
  ) {
    return normalized;
  }

  return 'dev_testing';
};

const getLatestReleaseStatusFromUpdates = (updates: unknown) => {
  if (!Array.isArray(updates)) {
    return null;
  }

  for (const entry of updates.slice().reverse()) {
    if (!isRecord(entry)) {
      continue;
    }

    const candidate =
      entry.releaseStatus ?? entry.status ?? entry.value ?? entry.to;

    if (candidate) {
      return candidate;
    }
  }

  return null;
};

const getParametersCount = (parameters: unknown) => {
  if (Array.isArray(parameters)) return parameters.length;
  if (isRecord(parameters)) return Object.keys(parameters).length;
  return 0;
};

export const toSiteComplianceCheckpointSettingsRow = (
  dto: CheckpointSettingDto,
): SiteComplianceCheckpointSettingsRow => {
  const checkpointId = asString(dto.checkpointId ?? dto.id);
  const checkpointVersion = asString(dto.version, '1.0.0');
  const isEnabled = asBoolean(dto.isEnabled, false);
  const isScored = isEnabled && asBoolean(dto.isScored, false);

  return {
    accountable: asString(dto.accountable),
    category: asString(dto.category),
    checkpointId,
    checkpointVersion,
    configurationId: asString(dto.id),
    description: asString(dto.description),
    displayName: asString(dto.name, checkpointId),
    exceptionEnabled: asBoolean(dto.serviceNowExceptionEnabled, false),
    id: asNullableString(dto.id),
    isEnabled,
    isScored,
    owner: asString(dto.accountable, 'Network'),
    parametersCount: getParametersCount(dto.parameters),
    passPercentageThreshold:
      dto.passPercentageThreshold === null ||
      dto.passPercentageThreshold === undefined
        ? null
        : asNumber(dto.passPercentageThreshold, 0),
    referenceSystem: asString(dto.referenceSystem),
    releaseStatus: normalizeReleaseStatus(
      dto.releaseStatus ?? getLatestReleaseStatusFromUpdates(dto.updates),
    ),
    unscoredStatusDetails: buildSettingsStatusDetails({
      ...dto,
      isEnabled,
      isScored,
    }),
    updatedAt: asString(dto.updatedAt ?? dto.createdAt),
    updatedBy: asString(dto.updatedBy ?? dto.createdBy),
    updates: Array.isArray(dto.updates) ? dto.updates : [],
    views: normalizeViews(dto.views),
  };
};

const toEvaluationPrevious = (
  previousDto: EvaluationSummaryPreviousDto | null | undefined,
): SiteComplianceEvaluationPrevious | null => {
  if (!previousDto) {
    return null;
  }

  return {
    generatedAt: asString(previousDto.generatedAt) || null,
    id: asString(previousDto.id) || null,
    inclusiveScore:
      typeof previousDto.inclusiveScore === 'number'
        ? normalizeScore(previousDto.inclusiveScore)
        : null,
    score:
      typeof previousDto.score === 'number'
        ? normalizeScore(previousDto.score)
        : null,
  };
};

export const toSiteComplianceEvaluation = (
  summaryDto: EvaluationSummaryDto,
): SiteComplianceEvaluation => {
  const siteId = asString(summaryDto.siteId);
  const generatedAt = asString(summaryDto.generatedAt);
  const counts = toEvaluationCountsFromSummary(summaryDto);

  return {
    counts,
    exceptionCount: counts.exceptions,
    generatedAt,
    id: asString(summaryDto.id),
    inclusiveScore: normalizeScore(summaryDto.inclusiveScore),
    platform: asString(summaryDto.platform) || null,
    previewCheckpoints: counts.unscored,
    previous: toEvaluationPrevious(summaryDto.previous),
    scoredCheckpoints: counts.scored,
    score: normalizeScore(summaryDto.score),
    siteId,
    totalCheckpoints: counts.total,
    totalEvaluations:
      typeof summaryDto.totalEvaluations === 'number'
        ? summaryDto.totalEvaluations
        : null,
  };
};

const splitMultiValue = (value: unknown) =>
  asString(value)
    .split(/[;,/]/)
    .map((entry) => entry.trim())
    .filter(Boolean);

const normalizeSiteType = (value: unknown): SiteComplianceSiteType => {
  const normalized = asString(value).trim().toLowerCase();

  if (
    normalized === 'office' ||
    normalized === 'office_plus' ||
    normalized === 'flex' ||
    normalized === 'datacenter'
  ) {
    return normalized;
  }

  return 'office';
};

const normalizeNetworkTier = (value: unknown): SiteComplianceNetworkTier => {
  const normalized = asString(value).trim().toLowerCase();

  if (
    normalized === 'standard' ||
    normalized === 'enhanced' ||
    normalized === 'flagship'
  ) {
    return normalized;
  }

  return null;
};

const inferCountryFromSiteId = (siteId: string) => {
  const parts = siteId.split('-');
  return parts.length > 1 && parts[1].length === 3 ? parts[1] : null;
};

const toSiteComplianceSite = (
  siteDto: EvaluationSiteSnapshotDto | null | undefined,
  detailDto: EvaluationDetailDto,
): SiteComplianceSite => {
  const siteId = asString(siteDto?.siteId ?? detailDto.siteId);
  const primaryDivisions = splitMultiValue(siteDto?.primaryDivision);
  const businessUnits = splitMultiValue(siteDto?.businessUnit);
  const networkTier = normalizeNetworkTier(siteDto?.networkTier);
  const siteType = normalizeSiteType(siteDto?.siteType);

  return {
    address: asNullableString(siteDto?.physicalAddress),
    businessUnits,
    country: inferCountryFromSiteId(siteId),
    currentCompliance:
      detailDto.score === null || detailDto.score === undefined
        ? null
        : normalizeScore(detailDto.score),
    evaluationId: asNullableString(detailDto.id),
    isDatacenter: siteType === 'datacenter',
    isEnhanced: networkTier === 'enhanced',
    isFlagship: networkTier === 'flagship',
    lastReviewedAt: asNullableString(detailDto.generatedAt),
    networkTier,
    primaryDivision: asNullableString(siteDto?.primaryDivision),
    primaryDivisions,
    region: asString(siteDto?.region),
    siteId,
    siteName: siteId || 'Site Compliance site',
    siteType,
    source: 'netbox',
    workdayId: asString(siteDto?.workdayId, siteId),
  };
};

const toCheckpointParameterType = (value: unknown): CheckpointParameterType => {
  const normalized = asString(value).trim().toLowerCase();

  if (normalized === 'boolean' || normalized === 'bool') {
    return CheckpointParameterType.Boolean;
  }
  if (normalized === 'integer' || normalized === 'int') {
    return CheckpointParameterType.Integer;
  }
  if (
    normalized === 'float' ||
    normalized === 'double' ||
    normalized === 'number'
  ) {
    return CheckpointParameterType.Float;
  }
  if (normalized === 'list' || normalized === 'array') {
    return CheckpointParameterType.List;
  }
  if (normalized === 'dictionary' || normalized === 'object') {
    return CheckpointParameterType.Dictionary;
  }

  return CheckpointParameterType.String;
};

const toCheckpointParameter = (
  parameter: SdkCheckpointParameterDto,
): CheckpointParameter => ({
  displayName: asString(parameter.displayName ?? parameter.name),
  isOptional: asBoolean(parameter.optional, false),
  isRuntime: asBoolean(parameter.isRuntime, false),
  paramDescription: asString(parameter.description),
  paramName: asString(parameter.name),
  paramType: toCheckpointParameterType(parameter.type),
  paramValue: '',
});

const getAssessmentItems = (
  items: AssessmentDto['items'],
): AssessmentItemDto[] => {
  if (Array.isArray(items)) return items;
  if (
    isRecord(items) &&
    ('status' in items || 'itemId' in items || 'remarks' in items)
  ) {
    return [items as AssessmentItemDto];
  }

  return [];
};

const toEvaluationItems = (
  assessment: AssessmentDto | null | undefined,
  checkpointId: string,
): EvaluationItem[] =>
  getAssessmentItems(assessment?.items).map((item, index) => ({
    checkpointId,
    itemId: asString(item.itemId, `${checkpointId}-item-${index + 1}`),
    itemType: asString(item.itemType, 'Site'),
    reference: asString(item.reference),
    remarks: asString(item.remarks),
    status: normalizeEvaluationStatus(item.status ?? assessment?.status),
    system: asString(item.system),
  }));

const toPrimitiveExceptionValue = (
  value: SiteComplianceJson,
): EvaluationExceptionDetails[string] => {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  return JSON.stringify(value);
};

const toExceptionDetails = (
  value: SiteComplianceJson | null | undefined,
): EvaluationExceptionDetails | null => {
  if (!isRecord(value) || Object.keys(value).length === 0) {
    return null;
  }

  return Object.entries(value).reduce<EvaluationExceptionDetails>(
    (acc, [key, entry]) => ({
      ...acc,
      [key]: toPrimitiveExceptionValue(entry as SiteComplianceJson),
    }),
    {},
  );
};

const toAssessmentDataRows = (
  assessment: AssessmentDto | null | undefined,
): EvaluationAssessmentDataItem[] => {
  if (!assessment) return [];

  const rows: EvaluationAssessmentDataItem[] = [
    {
      property: 'status',
      value: normalizeEvaluationStatus(assessment.status),
    },
    {
      property: 'remarks',
      value: asString(assessment.remarks),
    },
  ];

  if (isRecord(assessment.data)) {
    Object.entries(assessment.data).forEach(([property, value]) => {
      rows.push({
        property,
        value: value as EvaluationAssessmentDataItem['value'],
      });
    });
  }

  return rows;
};

const buildCheckpointScorePercent = ({
  assessmentStatus,
  isScored,
  items,
}: {
  assessmentStatus: EvaluationStatus;
  isScored: boolean;
  items: EvaluationItem[];
}) => {
  if (!isScored) return 0;

  const scoredStatuses = items
    .map((item) => item.status)
    .filter((status) => status !== 'NA');

  if (!scoredStatuses.length) {
    return assessmentStatus === 'PASS' ? 1 : 0;
  }

  const passed = scoredStatuses.filter((status) => status === 'PASS').length;
  return Number((passed / scoredStatuses.length).toFixed(2));
};

const toEvaluationCheckpoint = (
  checkpointDto: {
    assessment?: AssessmentDto | null;
    metadata?: CheckpointMetadataDto | null;
  },
): EvaluationCheckpoint => {
  const metadata = checkpointDto.metadata;
  const assessment = checkpointDto.assessment;
  const checkpointId = asString(metadata?.id ?? assessment?.checkpointId);
  const items = toEvaluationItems(assessment, checkpointId);
  const status = normalizeEvaluationStatus(assessment?.status);
  const isScored = asBoolean(metadata?.isScored, false);
  const hasException = asBoolean(assessment?.hasException, false);
  const totalControls = items.length;
  const unscoredStatusDetails = metadata?.unscoredStatusDetails;
  const unscoredType = normalizeUnscoredType(unscoredStatusDetails);
  const unscoredEndDate = asNullableString(
    getWindowValue(unscoredStatusDetails, 'endDate', 'end_date'),
  );
  const unscoredEventTime = asNullableString(
    getWindowValue(unscoredStatusDetails, 'eventTime', 'event_time'),
  );

  return {
    assessmentData: toAssessmentDataRows(assessment),
    exceptionDetails: toExceptionDetails(assessment?.exceptionDetails),
    group: asString(metadata?.group ?? metadata?.category),
    hasException,
    items,
    metadata: {
      accountable: asString(metadata?.accountable),
      category: asString(metadata?.category),
      checkpointVersion: asString(metadata?.checkpointVersion),
      description: asString(metadata?.description),
      displayName: asString(metadata?.name, checkpointId),
      enabled: asBoolean(metadata?.isEnabled, false),
      group: asString(metadata?.group ?? metadata?.category) || null,
      id: checkpointId,
      isEnabled: asBoolean(metadata?.isEnabled, false),
      isMaintenance: asBoolean(metadata?.isMaintenance, false),
      isScored,
      maintenance: asBoolean(metadata?.isMaintenance, false)
        ? {
            eventTime: unscoredEventTime ?? undefined,
            note: asString(unscoredStatusDetails?.reason) || undefined,
          }
        : undefined,
      owner: asString(metadata?.accountable, 'Network'),
      parameters: [],
      referenceSystem: asString(metadata?.referenceSystem),
      scorePercent: buildCheckpointScorePercent({
        assessmentStatus: status,
        isScored,
        items,
      }),
      totalAssessments: totalControls,
      totalControls,
      totalExemptedAssessments: hasException ? Math.max(totalControls, 1) : 0,
      totalPreviewControls: isScored ? 0 : totalControls,
      unscoredStatusDetails: unscoredStatusDetails
        ? {
            endDate: unscoredEndDate ?? undefined,
            eventTime: unscoredEventTime ?? undefined,
            reason: asString(unscoredStatusDetails.reason) || undefined,
            unscoredType: unscoredType || 'preview',
          }
        : undefined,
      url: '',
    },
    remarks: asString(assessment?.remarks),
    status,
    type: 'item',
  };
};

const toApplication = (site: SiteComplianceSite): Application => ({
  appOps: site.businessUnits.join(', ') || 'Network',
  bitId: site.siteId,
  dbOps: site.region,
  description: site.address,
  division: site.primaryDivisions.join(', ') || site.primaryDivision || '',
  hostingModel: 'Cloud',
  infraOps: site.region,
  isBusinessCritical: site.isDatacenter || site.isFlagship,
  name: site.siteName,
});

const KNOWN_AUTOMATION_PLATFORMS = new Set<string>(
  Object.values(AutomationPlatform),
);

const normalizePlatform = (value: unknown): AutomationPlatform => {
  const raw = asString(value);
  return KNOWN_AUTOMATION_PLATFORMS.has(raw)
    ? (raw as AutomationPlatform)
    : AutomationPlatform.UNKNOWN;
};

const toRequestHeaders = (
  headers: Record<string, string> | null | undefined,
  platform: AutomationPlatform,
) => ({
  'user-agent':
    isRecord(headers) && typeof headers['user-agent'] === 'string'
      ? headers['user-agent']
      : 'Site Compliance API',
  'x-orr-automation-definition':
    isRecord(headers) &&
    typeof headers['x-orr-automation-definition'] === 'string'
      ? headers['x-orr-automation-definition']
      : '',
  'x-orr-automation-platform': platform,
  'x-orr-caller':
    isRecord(headers) && typeof headers['x-orr-caller'] === 'string'
      ? headers['x-orr-caller']
      : 'site-compliance',
});

const toEvaluation = (
  detailDto: EvaluationDetailDto,
  site: SiteComplianceSite,
): Evaluation => {
  const checkpoints = toArray(detailDto.checkpoints).map((checkpoint) =>
    toEvaluationCheckpoint(checkpoint),
  );
  const platform = normalizePlatform(detailDto.platform);

  return {
    applicationName: site.siteName,
    args: (detailDto.request?.args ?? {}) as Evaluation['args'],
    assessmentStatus:
      normalizeScore(detailDto.score) >= 85 ? 'PASS' : 'FAIL',
    bitId: site.siteId,
    checkpoints,
    cloudAccounts: [],
    divisionName: site.primaryDivisions.join(', ') || site.primaryDivision || '',
    environmentNames: ['site'],
    extraSourceCloudAccounts: [],
    generatedAt: asString(detailDto.generatedAt),
    generatedBy: asString(detailDto.generatedBy),
    id: asString(detailDto.id),
    inclusiveScore: normalizeScore(detailDto.inclusiveScore),
    isLatest: asBoolean(detailDto.isLatest, false),
    isWKTenant: null,
    metadata: toApplication(site),
    platform,
    previous: {},
    request: {
      args: (detailDto.request?.args ?? {}) as Evaluation['request']['args'],
      bitId: site.siteId,
      environmentNames: ['site'],
      headers: toRequestHeaders(detailDto.request?.headers, platform),
    },
    schemaVersion: asString(detailDto.schemaVersion),
    score: normalizeScore(detailDto.score),
    totalCheckpoints: checkpoints.length,
    totalEvaluations: 1,
    totalScoredCheckpoints: checkpoints.filter(
      (checkpoint) => checkpoint.metadata.isScored,
    ).length,
  };
};

export const toSiteComplianceEvaluationDetails = (
  detailDto: EvaluationDetailDto,
): SiteComplianceEvaluationDetails => {
  const site = toSiteComplianceSite(detailDto.site, detailDto);
  const evaluation = toEvaluation(detailDto, site);
  const counts = deriveEvaluationCountsFromCheckpoints(evaluation.checkpoints);

  return {
    availableViews: DEFAULT_VIEWS,
    evaluation: {
      ...evaluation,
      totalCheckpoints: counts.total,
      totalScoredCheckpoints: counts.scored,
    },
    site,
  };
};

export const normalizeSiteComplianceJobStatus = (
  status: unknown,
): SiteComplianceJobStatus => {
  const normalized = asString(status, 'Pending')
    .trim()
    .replace(/[\s_-]/g, '')
    .toLowerCase();

  if (normalized === 'completed' || normalized === 'complete') {
    return 'Completed';
  }

  if (normalized === 'failed' || normalized === 'faulted') {
    return 'Failed';
  }

  if (normalized === 'timedout' || normalized === 'timeout') {
    return 'TimedOut';
  }

  if (normalized === 'running' || normalized === 'inprogress') {
    return 'Running';
  }

  return 'Pending';
};

const normalizeJobOutput = (
  output: unknown,
): SiteComplianceJobOutput | null => {
  if (!isRecord(output)) {
    return null;
  }

  const record = output as SiteComplianceJobOutput;

  return {
    ...record,
    bitId: asNullableString(record.bitId),
    duration: asNullableString(record.duration),
    evaluationId: asNullableString(record.evaluationId),
    message: asNullableString(record.message),
    recordId: asNullableString(record.recordId),
    runId: asNullableString(record.runId),
    status: asNullableString(record.status),
  };
};

const getEvaluationIdFromJobPayload = (
  payload: {
    evaluationId?: string | null;
    output?: SiteComplianceJobOutput | null;
  },
) =>
  asNullableString(
    payload.evaluationId ??
      payload.output?.evaluationId ??
      payload.output?.recordId,
  );

export const toSubmitEvaluationResponse = (
  dto: SubmitEvaluationResponseDto,
  siteId: string,
): SubmitEvaluationResponse => {
  const output = normalizeJobOutput(dto.output);

  return {
    evaluationId: getEvaluationIdFromJobPayload({
      evaluationId: dto.evaluationId,
      output,
    }),
    jobId: asString(dto.jobId),
    message: asNullableString(dto.message ?? output?.message),
    output,
    siteId: asString(dto.siteId, siteId),
    status: normalizeSiteComplianceJobStatus(dto.status),
    statusUrl: asString(dto.statusUrl),
  };
};

export const toSiteComplianceJobStatusResponse = (
  dto: JobStatusResponseDto,
  fallbackJobId: string,
): JobStatusResponse => {
  const output = normalizeJobOutput(dto.output);

  return {
    completedAt: asNullableString(dto.completedAt),
    evaluationId: getEvaluationIdFromJobPayload({
      evaluationId: dto.evaluationId,
      output,
    }),
    jobId: asString(dto.jobId, fallbackJobId),
    message: asNullableString(dto.message ?? output?.message),
    output,
    siteId: asNullableString(dto.siteId ?? output?.bitId),
    startedAt: asNullableString(dto.startedAt ?? dto.createdAt),
    status: normalizeSiteComplianceJobStatus(dto.status),
    statusUrl: asString(dto.statusUrl),
  };
};

const toPlainAssessmentValue = (value: unknown): unknown => {
  if (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value ?? null;
  }

  return JSON.stringify(value);
};

const normalizeAssessmentRow = (
  row: Record<string, unknown>,
  checkpointId: string,
  index: number,
) => {
  const normalized = Object.entries(row).reduce<Record<string, unknown>>(
    (acc, [key, value]) => {
      acc[key] = toPlainAssessmentValue(value);
      return acc;
    },
    {},
  );

  return {
    assessmentId:
      asString(
        normalized.assessmentId ??
          normalized.id ??
          normalized.itemId ??
          normalized.reference,
      ) || `${checkpointId}-${index + 1}`,
    ...normalized,
  };
};

const rowsFromUnknown = (
  value: unknown,
  checkpointId: string,
): Record<string, unknown>[] => {
  if (Array.isArray(value)) {
    return value.map((entry, index) => {
      if (isRecord(entry)) {
        return normalizeAssessmentRow(entry, checkpointId, index);
      }

      return {
        assessmentId: `${checkpointId}-${index + 1}`,
        value: toPlainAssessmentValue(entry),
      };
    });
  }

  if (!isRecord(value)) {
    return value === null || value === undefined
      ? []
      : [
          {
            assessmentId: `${checkpointId}-1`,
            value: toPlainAssessmentValue(value),
          },
        ];
  }

  const record = value as Record<string, unknown>;
  const candidateKeys = ['rows', 'assessmentData', 'assessments', 'items'];

  for (const key of candidateKeys) {
    const rows = rowsFromUnknown(record[key], checkpointId);

    if (rows.length > 0) {
      return rows;
    }
  }

  if (isRecord(record.data) || Array.isArray(record.data)) {
    const dataRows = rowsFromUnknown(record.data, checkpointId);

    if (dataRows.length > 0) {
      const base = {
        evaluatedAt: asNullableString(record.evaluatedAt),
        remarks: asNullableString(record.remarks),
        severity: asNullableString(record.severity),
        status: asNullableString(record.status),
      };

      return dataRows.map((row) => ({
        ...base,
        ...row,
      }));
    }
  }

  return Object.entries(record).map(([property, value], index) => ({
    assessmentId: `${checkpointId}-${index + 1}`,
    property,
    value: toPlainAssessmentValue(value),
  }));
};

export const toSiteComplianceAssessmentData = (
  dto: AssessmentDataDto,
  evaluationId: string,
  checkpointId: string,
): SiteComplianceAssessmentData => {
  const rows = rowsFromUnknown(dto, checkpointId);

  return {
    checkpointId: asString(dto.checkpointId, checkpointId),
    evaluationId,
    raw: dto,
    rows,
    score:
      dto.score === null || dto.score === undefined
        ? null
        : normalizeScore(dto.score),
    status: asNullableString(dto.status),
  };
};

export const buildSiteComplianceCheckpointExportRows = ({
  evaluation,
}: {
  evaluation: Evaluation;
}) =>
  evaluation.checkpoints.map((checkpoint) => {
    const counts = checkpoint.items.reduce(
      (acc, item) => ({
        error: acc.error + (item.status === 'ERROR' ? 1 : 0),
        failed: acc.failed + (item.status === 'FAIL' ? 1 : 0),
        passed: acc.passed + (item.status === 'PASS' ? 1 : 0),
        skipped: acc.skipped + (item.status === 'NA' ? 1 : 0),
      }),
      {
        error: 0,
        failed: 0,
        passed: 0,
        skipped: 0,
      },
    );

    return {
      assessmentIds:
        checkpoint.items.map((item) => item.itemId).filter(Boolean).join('; ') ||
        checkpoint.metadata.id,
      checkpointId: checkpoint.metadata.id,
      checkpointName: checkpoint.metadata.displayName,
      checkpointVersion:
        checkpoint.metadata.checkpointVersion ??
        checkpoint.metadata.version ??
        '',
      error: counts.error,
      exceptions: checkpoint.hasException ? 1 : 0,
      failed: counts.failed,
      isScored: checkpoint.metadata.isScored ? 'Yes' : 'No',
      passed: counts.passed,
      score: Math.round((checkpoint.metadata.scorePercent ?? 0) * 100),
      skipped: counts.skipped,
      status: checkpoint.status,
      totalAssessments: checkpoint.metadata.totalAssessments,
      totalControls: checkpoint.metadata.totalControls,
    };
  });

export const toCheckpointParameters = (
  parameters: SdkCheckpointParameterDto[] | null | undefined,
) => toArray(parameters).map(toCheckpointParameter);

export const toSeedCheckpointsResponse = (
  dto: SeedCheckpointsResponseDto,
): SeedCheckpointsResponse => ({
  created: asNumber(dto.created, 0),
  createdIds: toArray(dto.createdIds).map((id) => asString(id)),
  unchanged: asNumber(dto.unchanged, 0),
  unchangedIds: toArray(dto.unchangedIds).map((id) => asString(id)),
  updated: asNumber(dto.updated, 0),
  updatedIds: toArray(dto.updatedIds).map((id) => asString(id)),
});
